import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as amplitude from "@amplitude/analytics-browser";
import { initAmplitude } from "./Events";

// Emits a page view on every route change and a page exit carrying time spent on the
// page just left. Mounted once in App so every route is covered, including pages that
// never call Events() themselves and pages opened in a new tab via window.open.
const usePageTracking = () => {
  const location = useLocation();
  const previous = useRef(null);
  const enteredAt = useRef(null);

  useEffect(() => {
    // Returns false before login; the next route change retries.
    if (!initAmplitude()) return;

    // pathname only - the query string holds a base64 blob of params (see
    // Helper/QueryParams/EncryptDecrypt) and would be unique per visit.
    const path = location.pathname;
    const now = Date.now();

    if (previous.current && enteredAt.current) {
      amplitude.track("Page Exited", {
        page_path: previous.current,
        duration_seconds: Math.round((now - enteredAt.current) / 1000),
      });
    }

    amplitude.track("Page Viewed", {
      page_path: path,
      previous_page_path: previous.current,
    });

    previous.current = path;
    enteredAt.current = now;
  }, [location.pathname]);

  // Do not lose the last page's time when the tab is closed or backgrounded.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (previous.current && enteredAt.current) {
          amplitude.track("Page Exited", {
            page_path: previous.current,
            duration_seconds: Math.round((Date.now() - enteredAt.current) / 1000),
          });
          amplitude.flush();
        }
        enteredAt.current = null;
      } else if (previous.current) {
        enteredAt.current = Date.now();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
};

export default usePageTracking;
